import "./exportSection.css";

import {
  ANALYTICS_EVENTS,
  DEFAULT_BLACKLISTED_TRAITS,
  EXPORT_FORMATS,
} from "../../../constants";
import { Button } from "reactstrap";
import { PropTypes } from "prop-types";
import download from "downloadjs";
import { useSelector } from "react-redux";
import { useTracking } from "../../../analytics";

export const ExportSection = ({
  trial,
  selectedDate,
  features,
  totalCount,
}) => {
  const traits = useSelector(({ resultMap }) => resultMap.traitsListForMap);
  const { trackEvent } = useTracking();

  const exportData = (selectedFormat) => {
    const format = EXPORT_FORMATS[selectedFormat];
    const fileName = `${trial?.display_name}_${selectedDate}${format.ext}`;
    const traitNames = traits.reduce(
      (currentTraits, trait) => ({
        ...currentTraits,
        [trait.technical_name]: trait.name,
      }),
      {}
    );
    const traitValues = Object.values(traitNames);

    // Features are re-mapped for export changing the keys of technical trait names to their display name
    const remappedFeatures = features.map((feature) => {
      const { properties, displayId, id, experiment } = feature;
      return {
        cloverfieldId: id,
        id: displayId,
        experiment,
        ...Object.entries(properties)
          .filter(
            ([key]) => !DEFAULT_BLACKLISTED_TRAITS.includes(key.toLowerCase())
          )
          .reduce(
            (currentProps, [property, value]) => ({
              ...currentProps,
              [traitNames[property] ?? property]: value,
            }),
            {}
          ),
      };
    });

    let dataString = "";
    switch (selectedFormat) {
      case "JSON":
        dataString += JSON.stringify(remappedFeatures).trim();
        break;
      case "CSV":
        var replacer = (key, value) => {
          return value == null || value === "∅" ? "" : value;
        };
        const columns = new Set(
          remappedFeatures.flatMap((row) => Object.keys(row))
        );
        const header = Array.from(columns).sort((a, b) => {
          if (a === "cloverfieldId") return -1;
          if (b === "cloverfieldId") return 1;
          if (a === "id") return -1;
          if (b === "id") return 1;
          // Sort by is (not) trait then by alphabetical order
          const traitCompare =
            +traitValues.includes(a) - +traitValues.includes(b);
          return traitCompare === 0 // means that both or neither is a trait
            ? a.toLowerCase().localeCompare(b.toLowerCase())
            : traitCompare;
        });
        const csv = [
          header.join(";"), // header row first
          ...remappedFeatures.map((row) =>
            header
              .map((fieldName) =>
                JSON.stringify(row[fieldName], replacer).trim()
              )
              .join(";")
          ),
        ].join("\n");
        dataString += csv;
        break;
      default:
        break;
    }
    const indexOfDot = fileName.lastIndexOf(".");
    const fileExtension = fileName.slice(indexOfDot + 1);
    download(dataString, fileName, format.mime);

    trackEvent(ANALYTICS_EVENTS.ANALYTICS_DOWNLOAD_BUTTON, {
      downloadFormat: fileExtension,
    });
  };

  // Rendered at the bottom of the "Download results" drawer, under the
  // selection.
  return (
    <div className="export-section text-center">
      <div className="fw-bold">Filtered data</div>
      <div className="export-section-count mb-2">
        {features.length} / {totalCount} plots
      </div>
      {Object.keys(EXPORT_FORMATS).map((formatName) => (
        <Button
          key={formatName}
          color="link"
          className="p-0 mb-2 d-block mx-auto"
          onClick={() => exportData(formatName)}
          data-tooltip-id="tooltip"
          data-tooltip-content={`Download filtered and calculated data to ${formatName}`}
        >
          {formatName.toLowerCase()}
        </Button>
      ))}
    </div>
  );
};

ExportSection.propTypes = {
  features: PropTypes.array.isRequired,
  totalCount: PropTypes.number.isRequired,
  trial: PropTypes.object,
  selectedDate: PropTypes.string,
};
